import { FC, useCallback, useEffect, useState } from 'react';

import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { useProjectIdSearchParamOnMount } from 'modules/app/hooks/useInitializeCurrentProject/useProjectIdSearchParamOnMount';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';

import { FetchSnippetsQueryRequest, useSnippets } from '../hooks/useSnippets';
import { useSnippetFiltersContext } from './SnippetFiltersContext';
import { SnippetQueryContext, SnippetQueryContextState } from './SnippetQueryContext';

export const SnippetQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { currentPage, pageSize, setCurrentPage } = usePaginationContext();
  const { sortBy, sortDirection } = useSortingContext();
  const { searchQuery } = useSnippetFiltersContext();

  useProjectIdSearchParamOnMount();

  // GraphQL

  // Since the GraphQL queries rely on the pagination and sort data for each query, we need a helper with access to the local scope
  const getQueryVariables = useCallback(
    (): FetchSnippetsQueryRequest => ({
      pagination: {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      },
      search: searchQuery || null,
      sort: {
        sortBy,
        sortDirection,
      },
    }),
    [currentPage, pageSize, searchQuery, sortBy, sortDirection],
  );

  const Snippets = useSnippets(getQueryVariables);

  const {
    errorMessage = '',
    hasError = false,
    isLoading,
    paginationInfo = {
      count: 0,
      limit: 20,
      offset: 0,
      page: 1,
      __typename: GRAPHQL_TYPENAMES.INFO,
    },
    refetch = () => {
      // empty
    },
    results = [],
  } = Snippets;

  const refetchSnippets = () => {
    refetch(getQueryVariables());
  };

  // Used to track selected snippet from overflow menu
  const [selectedSnippet, setSelectedSnippet] = useState(null);

  // Whenever a tracked state that populates the graphQL variables changes, we need to refetch
  useEffect(() => {
    refetchSnippets();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    refetchSnippets();
  }, [pageSize, searchQuery, sortBy, sortDirection]);

  const context: SnippetQueryContextState = {
    errorMessage,
    hasError,
    isLoading,
    paginationInfo,
    refetch: refetchSnippets,
    snippets: results,
    setSelectedSnippet,
    selectedSnippet,
  };

  return <SnippetQueryContext.Provider value={context}>{children}</SnippetQueryContext.Provider>;
};
